// src/App.js
import React, { useState, useCallback, useEffect, createContext } from 'react';
import { Loader2 } from 'lucide-react';
import { onAuthStateChanged } from 'firebase/auth'; // Import only necessary auth functions for App.js
import { Toaster } from 'sonner'; // Import Toaster component

import { auth } from './firebase/firebase'; // Import auth instance
import AuthScreen from './components/AuthScreen'; // Import AuthScreen component
import CodeExecutorApp from './components/CodeExecutorApp'; // Import CodeExecutorApp component

// Create a Context for Firebase Auth to make user object globally available
export const AuthContext = createContext(null); // Export AuthContext

const App = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loadingAuth, setLoadingAuth] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUser(user);
            setLoadingAuth(false);
        });
        return () => unsubscribe(); // Cleanup subscription
    }, []);

    if (loadingAuth) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
                <Loader2 className="animate-spin w-10 h-10 text-blue-400" />
                <span className="ml-3 text-xl">Loading authentication...</span>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={currentUser}>
            {currentUser ? <CodeExecutorApp /> : <AuthScreen />}
            <Toaster richColors position="bottom-right" /> {/* Add Toaster component here */}
        </AuthContext.Provider>
    );
};

export default App;

        

// import React, { useState, useCallback, useEffect, createContext, useContext } from 'react';
// import { Button } from './components/ui/button';
// import { Input } from './components/ui/input'; // Still needed for methodName input
// import { Textarea } from './components/ui/textarea';
// import { Card, CardHeader, CardContent, CardTitle, CardDescription } from './components/ui/card';
// import { Alert, AlertTitle, AlertDescription } from './components/ui/alert';
// import { Loader2, CheckCircle, XCircle, Code, Terminal, ListChecks, Plus, LogOut, User, Mail, Globe } from 'lucide-react'; // Added Globe icon for Google
// import { cn } from './lib/utils';

// // --- Firebase Imports and Initialization ---
// import { initializeApp } from 'firebase/app';
// import {
//     getAuth,
//     GoogleAuthProvider, // <-- NEW: Import GoogleAuthProvider
//     signInWithPopup,    // <-- NEW: Import signInWithPopup
//     signOut,
//     onAuthStateChanged
// } from 'firebase/auth';
// import {
//     getFirestore,
//     doc,
//     getDoc,
//     setDoc,
//     collection,
//     query,
//     where,
//     getDocs,
//     Timestamp
// } from 'firebase/firestore';

// // TODO: REPLACE WITH YOUR ACTUAL FIREBASE CONFIGURATION
// // This should match the config you copied from your Firebase Project Settings -> Web App
// const firebaseConfig = {
//   apiKey: "AIzaSyCkCl2INEKwfG1LHBsR35rUFCQGqfoLdug",
//   authDomain: "methodaday.firebaseapp.com",
//   projectId: "methodaday",
//   storageBucket: "methodaday.appspot.com",
//   // messagingSenderId: "YOUR_MESSAGING_SENDER_ID",
//   appId: "methodaday"
// };

// const app = initializeApp(firebaseConfig);
// const auth = getAuth(app);
// const db = getFirestore(app);
// const googleProvider = new GoogleAuthProvider(); // <-- NEW: Create Google Auth Provider instance
// // --- End Firebase Imports and Initialization ---

// // Create a Context for Firebase Auth to make user object globally available
// const AuthContext = createContext(null);

// const problems = [
//     {
//         id: "sum-two-numbers",
//         title: "Sum of Two Numbers",
//         description: "Write a method that takes two integers as input and returns their sum.",
//         example: "sum(5, 3) → 8",
//         code: `    public static int sum(int a, int b) {
//         // Your code here
//         return a + b;
//     }`,
//         templateCode: `    public static int sum(int a, int b) {
//         // Your code here
//         return 0; // Implement your solution here
//     }`,
//         methodName: "sum",
//         tests: [
//             { input: [5, 3], expected: 8 },
//             { input: [-1, 10], expected: 9 },
//             { input: [0, 0], expected: 0 },
//         ],
//     },
//     {
//         id: "calculate-area",
//         title: "Calculate Area of a Rectangle",
//         description: "Write a method that calculates the area of a rectangle given its length and width.",
//         example: "area(4, 5) -> 20",
//         code: `    public static int area(int length, int width) {
//         // Your code here
//         return length * width;
//     }`,
//         templateCode: `    public static int area(int length, int width) {
//         // Your code here
//         return 0; // Implement your solution here
//     }`,
//         methodName: "area",
//         tests: [
//             { input: [4, 5], expected: 20 },
//             { input: [10, 10], expected: 100 },
//             { input: [2, 3], expected: 6 },
//         ],
//     },
//     {
//         id: "reverse-string",
//         title: "Reverse a String",
//         description: "Write a method that takes a string as input and returns the reversed string.",
//         example: "reverseString(\"hello\") -> \"olleh\"",
//         code: `    public static String reverseString(String str) {
//         // Your code here
//         return new StringBuilder(str).reverse().toString();
//     }`,
//         templateCode: `    public static String reverseString(String str) {
//         // Your code here
//         return ""; // Implement your solution here
//     }`,
//         methodName: "reverseString",
//         tests: [
//             { input: ["hello"], expected: "olleh" },
//             { input: ["world"], expected: "dlrow" },
//             { input: ["abc"], expected: "cba" },
//         ],
//     },
//     {
//         id: "find-max-number",
//         title: "Find the Maximum Number",
//         description: "Write a method that takes an array of integers and returns the maximum number.",
//         example: "findMax([1, 5, 2, 8, 3]) -> 8",
//         code: `    public static int findMax(int[] arr) {
//         // Your code here
//         if (arr == null || arr.length == 0) throw new IllegalArgumentException("Array cannot be empty");
//         int max = arr[0];
//         for (int i = 1; i < arr.length; i++) {
//             if (arr[i] > max) {
//                 max = arr[i];
//             }
//         }
//         return max;
//     }`,
//         templateCode: `    public static int findMax(int[] arr) {
//         // Your code here
//         // Implement your solution here
//         return 0;
//     }`,
//         methodName: "findMax",
//         tests: [
//             { input: [[1, 5, 2, 8, 3]], expected: 8 },
//             { input: [[10, 20, 5, 15]], expected: 20 },
//             { input: [[-5, -10, -2]], expected: -2 },
//             { input: [[]], expected: 0 },
//         ],
//     },
//     {
//         id: "check-if-prime",
//         title: "Check if a Number is Prime",
//         description: "Write a method that takes an integer and returns true if it's a prime number, false otherwise.",
//         example: "isPrime(7) → true",
//         code: `    public static boolean isPrime(int n) {
//         // Your code here
//         if (n <= 1) return false;
//         for (int i = 2; i * i <= n; i++) {
//             if (n % i == 0) return false;
//         }
//         return true;
//     }`,
//         templateCode: `    public static boolean isPrime(int n) {
//         // Your code here
//         return false; // Implement your solution here
//     }`,
//         methodName: "isPrime",
//         tests: [
//             { input: [7], expected: true },
//             { input: [12], expected: false },
//             { input: [2], expected: true },
//             { input: [1], expected: false },
//         ],
//     },
// ];

// // Main App Component with Auth Context Provider
// const App = () => {
//     const [currentUser, setCurrentUser] = useState(null);
//     const [loadingAuth, setLoadingAuth] = useState(true); // State to track if auth is still loading

//     useEffect(() => {
//         const unsubscribe = onAuthStateChanged(auth, (user) => {
//             setCurrentUser(user);
//             setLoadingAuth(false);
//         });
//         return () => unsubscribe(); // Cleanup subscription
//     }, []);

//     if (loadingAuth) {
//         return (
//             <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center">
//                 <Loader2 className="animate-spin w-10 h-10 text-blue-400" />
//                 <span className="ml-3 text-xl">Loading authentication...</span>
//             </div>
//         );
//     }

//     return (
//         <AuthContext.Provider value={currentUser}>
//             {currentUser ? <CodeExecutorApp /> : <AuthScreen />}
//         </AuthContext.Provider>
//     );
// };

// // Authentication Screen Component
// const AuthScreen = () => {
//     const [authError, setAuthError] = useState(null);
//     const [authLoading, setAuthLoading] = useState(false);

//     const handleGoogleSignIn = async () => {
//         setAuthError(null);
//         setAuthLoading(true);
//         try {
//             await signInWithPopup(auth, googleProvider);
//             // On successful sign-in, onAuthStateChanged listener in App component will update currentUser
//         } catch (error) {
//             setAuthError(error.message);
//             console.error("Google Sign-In Error:", error);
//         } finally {
//             setAuthLoading(false);
//         }
//     };

//     return (
//         <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white flex items-center justify-center p-4">
//             <Card className="w-full max-w-md bg-white/5 backdrop-blur-md border border-white/10 shadow-lg p-6 rounded-lg space-y-6">
//                 <CardHeader className="text-center">
//                     <CardTitle className="text-3xl font-bold">Welcome to Code Executor</CardTitle>
//                     <CardDescription className="text-gray-400">
//                         Sign in with your Google account to get started.
//                     </CardDescription>
//                 </CardHeader>
//                 <CardContent className="space-y-4">
//                     <Button
//                         onClick={handleGoogleSignIn}
//                         disabled={authLoading}
//                         className={cn(
//                             "w-full bg-blue-600 text-white px-6 py-3 rounded-md shadow-lg",
//                             "hover:bg-blue-700 transition-all duration-300",
//                             "disabled:opacity-50 disabled:cursor-not-allowed",
//                             "flex items-center justify-center gap-2"
//                         )}
//                     >
//                         {authLoading ? (
//                             <>
//                                 <Loader2 className="animate-spin w-5 h-5" />
//                                 Signing in with Google...
//                             </>
//                         ) : (
//                             <>
//                                 <Globe className="w-5 h-5" />
//                                 Sign in with Google
//                             </>
//                         )}
//                     </Button>
//                     {authError && (
//                         <Alert variant="destructive" className="bg-red-500/10 text-red-400 border-red-500/50 mt-4">
//                             <XCircle className="h-4 w-4" />
//                             <AlertTitle>Authentication Error</AlertTitle>
//                             <AlertDescription>{authError}</AlertDescription>
//                         </Alert>
//                     )}
//                 </CardContent>
//             </Card>
//         </div>
//     );
// };


// // CodeExecutorApp (Main application logic, now a separate component)
// const CodeExecutorApp = () => {
//     const currentUser = useContext(AuthContext); // Get current user from context
//     // UserId will always be present if CodeExecutorApp is rendered, as it's protected by AuthContext
//     const userId = currentUser.uid;

//     const [currentProblemIndex, setCurrentProblemIndex] = useState(0);
//     const [code, setCode] = useState('');
//     const [methodName, setMethodName] = useState('');
//     const [output, setOutput] = useState(''); // For compilation errors, console output, general messages
//     const [detailedTestResults, setDetailedTestResults] = useState([]); // For structured test results table
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState(null);
//     const [studentSolutions, setStudentSolutions] = useState({}); // Stores which problems are completed

//     // Function to display the current problem
//     const displayProblem = useCallback(async () => {
//         const problem = problems[currentProblemIndex];
//         setCode(`public class Solution {\n${problem.templateCode}\n}`);
//         setMethodName(problem.methodName);
//         setOutput('');
//         setDetailedTestResults([]);

//         // Load user's progress for this specific problem (optional, if you want to load previous code)
//         // const problemDocRef = doc(db, `users/${userId}/problems_solved`, problems[currentProblemIndex].id);
//         // const problemSnap = await getDoc(problemDocRef);
//         // if (problemSnap.exists()) {
//         //     const data = problemSnap.data();
//         //     if (data.savedCode) {
//         //         setCode(data.savedCode);
//         //     }
//         // }
//     }, [currentProblemIndex]); // userId is now in effect for loading all progress

//     // Function to update and persist progress
//     const updateProgress = useCallback(async (problemIndex, completed) => {
//         // Update local state
//         setStudentSolutions(prevSolutions => {
//             const newSolutions = { ...prevSolutions, [problemIndex]: completed };
//             localStorage.setItem('studentSolutions', JSON.stringify(newSolutions)); // Still keep local storage for faster UI updates
//             return newSolutions;
//         });

//         // Persist to Firestore
//         const problemDocRef = doc(db, `users/${userId}/problems_solved`, problems[problemIndex].id); // Using problem.id now
//         await setDoc(problemDocRef, {
//             completed: completed,
//             timestamp: Timestamp.now(),
//             problemTitle: problems[problemIndex].title
//             // Optionally save the code as well: savedCode: code
//         }, { merge: true });
//     }, [userId]);

//     // Effect to load ALL solved problems for the current user when auth state changes or on initial load
//     useEffect(() => {
//         const loadAllUserProgress = async () => {
//             if (currentUser && userId) { // Ensure currentUser and userId are available
//                 const solvedProblemsColRef = collection(db, `users/${userId}/problems_solved`);
//                 const q = query(solvedProblemsColRef);
//                 const querySnapshot = await getDocs(q);
//                 const loadedSolutions = {};
//                 querySnapshot.forEach((docSnap) => {
//                     loadedSolutions[docSnap.id] = docSnap.data().completed;
//                 });
//                 setStudentSolutions(loadedSolutions);

//                 // Find the highest completed problem index to set currentProblemIndex
//                 let highestCompletedIndex = -1;
//                 for (const problemId in loadedSolutions) {
//                     if (loadedSolutions[problemId] === true) {
//                         const index = problems.findIndex(p => p.id === problemId);
//                         if (index !== -1 && index > highestCompletedIndex) {
//                             highestCompletedIndex = index;
//                         }
//                     }
//                 }
//                 // Set the current problem to the next one if some are completed
//                 if (highestCompletedIndex !== -1 && (highestCompletedIndex + 1) < problems.length) {
//                     setCurrentProblemIndex(highestCompletedIndex + 1);
//                 } else if (highestCompletedIndex === problems.length - 1) {
//                     // All problems completed, stay on the last one or go back to first
//                     setCurrentProblemIndex(problems.length - 1);
//                 } else {
//                     setCurrentProblemIndex(0); // Start from the first if no progress or first problem not solved
//                 }

//             } else {
//                 // If not logged in, clear Firestore-based progress and rely on local storage (or just clear)
//                 localStorage.removeItem('studentSolutions');
//                 setStudentSolutions({});
//                 setCurrentProblemIndex(0); // Reset to first problem for unauthenticated users
//             }
//         };

//         loadAllUserProgress();
//         // displayProblem() is called in the loadAllUserProgress callback after setting currentProblemIndex
//     }, [currentUser, userId]); // Re-run when currentUser or userId changes, will trigger displayProblem via setCurrentProblemIndex

//     // useEffect to call displayProblem whenever currentProblemIndex changes
//     useEffect(() => {
//         displayProblem();
//     }, [currentProblemIndex, displayProblem]);


//     const handleExecute = useCallback(async () => {
//         setLoading(true);
//         setError(null);
//         setOutput('');
//         setDetailedTestResults([]);

//         const problem = problems[currentProblemIndex];
//         const fullUserClassCode = code;
//         let extractedCode = fullUserClassCode;
//         const classStart = "public class Solution {";
//         const classEnd = "}";

//         if (fullUserClassCode.startsWith(classStart) && fullUserClassCode.endsWith(classEnd)) {
//             extractedCode = fullUserClassCode.substring(
//                 classStart.length,
//                 fullUserClassCode.length - classEnd.length
//             ).trim();
//         }

//         const requestBody = {
//             code: extractedCode,
//             methodName: problem.methodName,
//             testCases: problem.tests,
//         };

//         try {
//             const response = await fetch('http://localhost:8080/execute', {
//                 method: 'POST',
//                 headers: {
//                     'Content-Type': 'application/json',
//                 },
//                 body: JSON.stringify(requestBody),
//             });

//             if (!response.ok) {
//                 let errorDetails = `HTTP error! Status: ${response.status}`;
//                 try {
//                     const errorData = await response.json();
//                     errorDetails += `. Details: ${errorData.errors || JSON.stringify(errorData)}`;
//                 } catch (e) {
//                     errorDetails += `. Response text: ${await response.text()}`;
//                 }
//                 throw new Error(errorDetails);
//             }

//             const data = await response.json();
//             console.log("Backend Response:", data);

//             let generalOutputMessages = '';
//             let allTestsPassed = true;

//             if (data.errors) {
//                 generalOutputMessages += `Compilation/Execution Error:\n${data.errors}\n\n`;
//                 allTestsPassed = false;
//             }

//             if (data.output) {
//                 generalOutputMessages += `Backend Console Output:\n${data.output}\n\n`;
//             }

//             if (data.testResults && data.testResults.length > 0) {
//                 setDetailedTestResults(data.testResults);
//                 const backendAllTestsPassed = data.testResults.every(testResult => testResult.passed);
//                 if (!backendAllTestsPassed) {
//                     allTestsPassed = false;
//                 }
//             } else {
//                 generalOutputMessages += "No detailed test results received from the backend. Ensure your backend is sending 'testResults'.\n";
//                 if (!data.errors && !data.output) {
//                     allTestsPassed = false;
//                 }
//             }

//             if (allTestsPassed && !data.errors) {
//                 generalOutputMessages += `Congratulations! You solved the problem.`;
//                 await updateProgress(currentProblemIndex, true);
//                 const nextProblemIndex = currentProblemIndex + 1;
//                 if (nextProblemIndex < problems.length) {
//                     generalOutputMessages += ` Moving on to the next problem: ${problems[nextProblemIndex].title}`;
//                     setTimeout(() => {
//                         setCurrentProblemIndex(nextProblemIndex);
//                     }, 2000);
//                 } else {
//                     generalOutputMessages += ` You have completed all the problems for today!`;
//                 }
//             } else {
//                 generalOutputMessages += `Issues detected. Please review the results.`;
//                 await updateProgress(currentProblemIndex, false);
//             }
            
//             setOutput(generalOutputMessages.trim());

//         } catch (error) {
//             setError(error.message);
//             setOutput(`Error: ${error.message}`);
//         } finally {
//             setLoading(false);
//         }
//     }, [code, currentProblemIndex, updateProgress]);

//     const handleLogout = useCallback(async () => {
//         try {
//             await signOut(auth);
//             // The onAuthStateChanged listener in App will handle state reset
//         } catch (error) {
//             setError("Logout failed: " + error.message);
//         }
//     }, []);

//     return (
//         <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white p-4 md:p-8">
//             <div className="max-w-7xl mx-auto space-y-6">
//                 <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
//                     Code Executor
//                 </h1>

//                 <div className="flex justify-between items-center bg-white/5 backdrop-blur-md border border-white/10 shadow-lg p-4 rounded-lg">
//                     <div className="flex items-center gap-2">
//                         <User className="w-5 h-5 text-gray-400" />
//                         <span className="text-sm text-gray-300">Logged in as: <span className="font-semibold">{currentUser?.email || 'N/A'}</span></span>
//                         <span className="text-xs text-gray-500">(UID: {currentUser?.uid || 'N/A'})</span>
//                     </div>
//                     <Button
//                         onClick={handleLogout}
//                         variant="ghost"
//                         className="text-red-400 hover:bg-red-500/20 hover:text-red-300 flex items-center gap-1"
//                     >
//                         <LogOut className="w-4 h-4" />
//                         Logout
//                     </Button>
//                 </div>

//                 <Card className="bg-white/5 backdrop-blur-md border border-white/10 shadow-lg">
//                     <CardHeader>
//                         <CardTitle className="text-lg sm:text-xl text-white flex items-center gap-2">
//                             <Code className="w-5 h-5" />
//                             Today's Problem: {problems[currentProblemIndex].title}
//                         </CardTitle>
//                         <CardDescription className="text-gray-400">
//                             {problems[currentProblemIndex].description}
//                             <br/>
//                             <strong>Example:</strong> {problems[currentProblemIndex].example}
//                         </CardDescription>
//                     </CardHeader>
//                 </Card>

//                 <div className="flex flex-col md:flex-row gap-6">
//                     <Card className="bg-white/5 backdrop-blur-md border border-white/10 shadow-lg flex-1">
//                         <CardHeader>
//                             <CardTitle className="text-lg sm:text-xl text-white flex items-center gap-2">
//                                 <Code className="w-5 h-5" />
//                                 Your Code
//                             </CardTitle>
//                             <CardDescription className="text-gray-400">Write your Java code here. Define a class named 'Solution' and a public method within it.</CardDescription>
//                         </CardHeader>
//                         <CardContent>
//                             <label htmlFor="code-editor" className="block text-sm font-medium text-gray-300 mb-1">Code:</label>
//                             <Textarea
//                                 id="code-editor"
//                                 value={code}
//                                 onChange={(e) => setCode(e.target.value)}
//                                 className="min-h-[200px] md:min-h-[300px] bg-black/80 text-white border-gray-700 rounded-md shadow-inner
//                                         focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
//                                         resize-y font-mono text-sm"
//                                 placeholder="// Write your code here"
//                             />
//                             <div className="mt-4">
//                                 <label htmlFor="methodName" className="block text-sm font-medium text-gray-300 mb-1">
//                                     Method Name to Execute (from problem description):
//                                 </label>
//                                 <Input
//                                     id="methodName"
//                                     type="text"
//                                     value={methodName}
//                                     readOnly
//                                     className="bg-black/80 text-white border-gray-700 rounded-md
//                                             focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
//                                             w-full opacity-70 cursor-not-allowed"
//                                     placeholder="Method name"
//                                 />
//                             </div>
//                         </CardContent>
//                     </Card>

//                     <Card className="bg-white/5 backdrop-blur-md border border-white/10 shadow-lg flex-1">
//                         <CardHeader>
//                             <CardTitle className="text-lg sm:text-xl text-white flex items-center gap-2">
//                                 <Terminal className="w-5 h-5" />
//                                 Output & Test Results
//                             </CardTitle>
//                             <CardDescription className="text-gray-400">Review compilation output and individual test case results.</CardDescription>
//                         </CardHeader>
//                         <CardContent>
//                             {output && (
//                                 <div className="mb-4">
//                                     <h3 className="text-md font-semibold text-gray-200 mb-2">General Output:</h3>
//                                     <Textarea
//                                         value={output}
//                                         readOnly
//                                         className="min-h-[100px] bg-black/80 text-yellow-400 border-gray-700 rounded-md shadow-inner
//                                                 font-mono text-sm whitespace-pre-wrap"
//                                         placeholder="Compilation messages and console output will appear here."
//                                     />
//                                 </div>
//                             )}

//                             {detailedTestResults.length > 0 && (
//                                 <div>
//                                     <h3 className="text-md font-semibold text-gray-200 mb-2">Test Case Results:</h3>
//                                     <div className="overflow-x-auto rounded-md border border-gray-700">
//                                         <table className="w-full text-left text-sm text-gray-400">
//                                             <thead className="text-xs uppercase bg-gray-700 text-gray-300">
//                                                 <tr>
//                                                     <th scope="col" className="px-4 py-2 whitespace-nowrap">Test Input</th>
//                                                     <th scope="col" className="px-4 py-2 whitespace-nowrap">Actual Output</th>
//                                                     <th scope="col" className="px-4 py-2 whitespace-nowrap">Expected Output</th>
//                                                     <th scope="col" className="px-4 py-2 text-center whitespace-nowrap">Status</th>
//                                                 </tr>
//                                             </thead>
//                                             <tbody>
//                                                 {detailedTestResults.map((testResult, index) => (
//                                                     <tr key={index} className="border-b bg-black/80 border-gray-700 hover:bg-gray-700/50">
//                                                         <td className="px-4 py-2 font-mono whitespace-nowrap">
//                                                             {JSON.stringify(testResult.input)}
//                                                         </td>
//                                                         <td className="px-4 py-2 font-mono whitespace-nowrap">
//                                                             {testResult.actual !== undefined && testResult.actual !== null ? testResult.actual.toString() : 'null'}
//                                                         </td>
//                                                         <td className="px-4 py-2 font-mono whitespace-nowrap">
//                                                             {testResult.expected !== undefined && testResult.expected !== null ? testResult.expected.toString() : 'null'}
//                                                         </td>
//                                                         <td className="px-4 py-2 text-center whitespace-nowrap">
//                                                             {testResult.passed ? (
//                                                                 <CheckCircle className="w-5 h-5 text-green-500 mx-auto" />
//                                                             ) : (
//                                                                 <XCircle className="w-5 h-5 text-red-500 mx-auto" />
//                                                             )}
//                                                         </td>
//                                                     </tr>
//                                                 ))}
//                                             </tbody>
//                                         </table>
//                                     </div>
//                                 </div>
//                             )}
//                             {output === '' && detailedTestResults.length === 0 && !loading && (
//                                 <p className="text-gray-500 italic">Run your code to see output and test results here.</p>
//                             )}
//                         </CardContent>
//                     </Card>
//                 </div>

//                 <div className="flex justify-center">
//                     <Button
//                         onClick={handleExecute}
//                         disabled={loading}
//                         className={cn(
//                             "bg-gradient-to-r from-blue-500 to-purple-500 text-white px-6 py-3 rounded-full shadow-lg",
//                             "hover:from-blue-600 hover:to-purple-600 hover:scale-105 transition-all duration-300",
//                             "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100",
//                             "flex items-center gap-2"
//                         )}
//                     >
//                         {loading ? (
//                             <>
//                                 <Loader2 className="animate-spin w-5 h-5" />
//                                 Running...
//                             </>
//                         ) : (
//                             'Run Code'
//                         )}
//                     </Button>
//                 </div>

//                 {error && (
//                     <Alert variant="destructive" className="bg-red-500/10 text-red-400 border-red-500/50">
//                         <XCircle className="h-4 w-4" />
//                         <AlertTitle>Error</AlertTitle>
//                         <AlertDescription>{error}</AlertDescription>
//                     </Alert>
//                 )}

//                 <Card className="bg-white/5 backdrop-blur-md border border-white/10 shadow-lg">
//                     <CardHeader>
//                         <CardTitle className="text-lg sm:text-xl text-white flex items-center gap-2">
//                             <ListChecks className="w-5 h-5"/>
//                             Your Progress
//                         </CardTitle>
//                     </CardHeader>
//                     <CardContent>
//                         <ul className="space-y-2">
//                             {problems.map((problem, index) => (
//                                 <li key={index} className="flex justify-between items-center text-gray-300">
//                                     <span>{problem.title}</span>
//                                     <span className={cn(
//                                         "font-semibold",
//                                         studentSolutions[index] ? "text-green-400" : "text-red-400"
//                                     )}>
//                                         {studentSolutions[index] ? "Completed" : "Not Completed"}
//                                     </span>
//                                 </li>
//                             ))}
//                         </ul>
//                     </CardContent>
//                 </Card>

//             </div>
//         </div>
//     );
// };

// export default App; // Export the main App component
